/*
 * Newsletter Split Section
 * - Prevents native page navigation in the capture phase.
 * - Submits Shopify's customer/newsletter form with fetch.
 * - Confirms a real Shopify success response before showing the success card.
 * - Detects Shopify /challenge or hCaptcha responses and keeps the visitor on-page.
 */
(function () {
  'use strict';

  var SELECTOR = 'form.nsl-form-row';
  var initialized = new WeakSet();

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function getElements(form) {
    var wrapper = form.closest('[data-nsl-form-wrapper]');
    var card = form.closest('[data-nsl-card]');

    return {
      wrapper: wrapper,
      card: card,
      success: card ? card.querySelector('[data-nsl-success]') : null,
      disclaimer: card ? card.querySelector('[data-nsl-disclaimer]') : null,
      button: form.querySelector('.nsl-button'),
      input: form.querySelector('.nsl-input'),
      error: form.querySelector('[data-nsl-error]')
    };
  }

  function showError(elements, message, type) {
    if (!elements.error) return;
    elements.error.textContent = message;
    elements.error.hidden = false;
    elements.error.classList.toggle('nsl-error--challenge', type === 'challenge');
    elements.error.setAttribute('role', 'alert');
  }

  function clearError(elements) {
    if (!elements.error) return;
    elements.error.hidden = true;
    elements.error.textContent = '';
    elements.error.classList.remove('nsl-error--challenge');
  }

  function setLoading(elements, loading) {
    if (!elements.button) return;

    if (loading) {
      if (!elements.button.dataset.originalHtml) {
        elements.button.dataset.originalHtml = elements.button.innerHTML;
      }
      elements.button.disabled = true;
      elements.button.classList.add('is-loading');
      elements.button.setAttribute('aria-busy', 'true');
      elements.button.innerHTML = '<span class="nsl-button-spinner" aria-hidden="true"></span><span>Subscribing...</span>';
    } else {
      elements.button.disabled = false;
      elements.button.classList.remove('is-loading');
      elements.button.removeAttribute('aria-busy');
      elements.button.innerHTML = elements.button.dataset.originalHtml || elements.button.innerHTML;
    }
  }

  function showSuccess(elements) {
    clearError(elements);

    if (elements.wrapper) {
      elements.wrapper.hidden = true;
      elements.wrapper.style.display = 'none';
    }

    if (elements.disclaimer) {
      elements.disclaimer.hidden = true;
      elements.disclaimer.style.display = 'none';
    }

    if (elements.success) {
      elements.success.hidden = false;
      elements.success.classList.add('is-visible');
      elements.success.setAttribute('tabindex', '-1');
      elements.success.focus({ preventScroll: true });
    }
  }

  function responseIsChallenge(response, html) {
    var finalUrl = (response.url || '').toLowerCase();
    var body = (html || '').toLowerCase();

    return finalUrl.indexOf('/challenge') !== -1 ||
      body.indexOf('hcaptcha') !== -1 ||
      body.indexOf('shopify-captcha') !== -1 ||
      body.indexOf("you're not a robot") !== -1 ||
      body.indexOf('i am human') !== -1;
  }

  function responseIsSuccess(response, html) {
    var finalUrl = (response.url || '').toLowerCase();
    var body = (html || '').toLowerCase();

    /* Explicit Shopify success indicators. */
    if (finalUrl.indexOf('contact_posted=true') !== -1) return true;
    if (finalUrl.indexOf('customer_posted=true') !== -1) return true;
    if (body.indexOf('data-nsl-server-status="success"') !== -1) return true;
    if (body.indexOf("data-nsl-server-status='success'") !== -1) return true;

    /*
     * On a published storefront Shopify can complete a customer/newsletter
     * POST and return a normal 200 page without preserving the Liquid success
     * marker in the fetched HTML. A successful non-challenge redirect or any
     * successful 2xx response is therefore accepted after server-error and
     * challenge checks have already run.
     */
    if (response.redirected && response.ok) return true;
    if (response.ok) return true;

    return false;
  }

  function extractServerError(html) {
    if (!html) return '';

    try {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var node = parsed.querySelector('[data-nsl-server-error]');
      return node ? node.textContent.trim() : '';
    } catch (error) {
      return '';
    }
  }

  async function submitNewsletter(form) {
    if (form.dataset.nslSubmitting === 'true') return;

    var elements = getElements(form);
    var email = elements.input ? elements.input.value.trim() : '';

    clearError(elements);

    if (!isValidEmail(email)) {
      showError(elements, 'Please enter a valid email address.');
      if (elements.input) elements.input.focus();
      return;
    }

    form.dataset.nslSubmitting = 'true';
    setLoading(elements, true);

    var controller = new AbortController();
    var timeoutId = window.setTimeout(function () {
      controller.abort();
    }, 20000);

    try {
      var response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        redirect: 'follow',
        headers: {
          'Accept': 'text/html,application/xhtml+xml',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: controller.signal
      });

      var html = await response.text();

      if (responseIsChallenge(response, html)) {
        showError(
          elements,
          'Shopify blocked this signup with a verification challenge. The page has not been redirected. Please try again later or contact the store.',
          'challenge'
        );
        return;
      }

      if (responseIsSuccess(response, html)) {
        showSuccess(elements);
        form.reset();
        return;
      }

      var serverError = extractServerError(html);
      showError(elements, serverError || 'Shopify could not process this subscription right now. Please try again shortly.');
    } catch (error) {
      if (error && error.name === 'AbortError') {
        showError(elements, 'The request took too long. Please try again.');
      } else {
        showError(elements, 'Network error. Please check your connection and try again.');
      }
    } finally {
      window.clearTimeout(timeoutId);
      form.dataset.nslSubmitting = 'false';
      setLoading(elements, false);
    }
  }

  function initializeForm(form) {
    if (!form || initialized.has(form)) return;
    initialized.add(form);

    var status = form.querySelector('[data-nsl-server-status="success"]');
    if (status) showSuccess(getElements(form));
  }

  function initializeAll(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(initializeForm);
  }

  /* Capture phase runs before Ella/theme bubble-phase newsletter handlers. */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest ? event.target.closest(SELECTOR) : null;
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    submitNewsletter(form);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeAll(document);
    });
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });
})();
